import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

import {
  invoiceNumber,
  billingCycleStartDate,
  billingCycleEndDate,
  invoiceDate,
  invoiceSummary,
  download,
  preview,
} from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';

function InvoiceList() {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const store = useStore();

  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(null);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/invoices`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'INVOICE_LIST_BREADCRUMB',
        defaultMessage: 'List Invoices',
      }),
      icon: IconLibrary.Invoices,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('InvoiceList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'InvoiceList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    sortBy: 'invoiceDate',
    sortOrder: 'DESC',
    accessorFunctions: {
      invoiceNumber,
      billingCycleStartDate,
      billingCycleEndDate,
      invoiceDate,
      invoiceSummary,
      download,
      preview,
    },
    accessorFunctionsAdditional: {
      getApiUrl: () => dataProvider.getApiUrl(),
      get: (...args) => dataProvider.get(...args),
      formatMessage,
      snackbar,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: 'InvoiceList',
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset('InvoiceList');
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName }) => {
    switch (searchName) {
      default:
        return null;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: 'InvoiceList',
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'INVOICE_SEARCH_HEADING',
            defaultMessage: 'Search Invoices',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'INVOICE_LIST_HEADING',
          defaultMessage: 'Invoices',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(InvoiceList);
